// SEO landing page data. Each entry drives a full page via ServiceLandingPage.jsx.
// Slug (with & without trailing slash) → data object.
// Themes: unique accent + hero image per page for visual differentiation.

export const landingPages = {
  'performance-marketing-consultant': {
    slug: 'performance-marketing-consultant',
    theme: {
      accent: '#0ea5e9',
      accentSoft: 'rgba(14, 165, 233, 0.14)',
      accentStrong: '#0284c7',
    },
    heroImage: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1400&q=85&auto=format',
    meta: {
      title: 'Performance Marketing Consultant | Sanjib Roy — 16 Years, ROI-First',
      description: 'Independent Performance Marketing Consultant helping global B2B and D2C brands scale profitable paid media across Google, Meta, LinkedIn and programmatic — with 16+ years of hands-on operator experience.',
      keywords: 'performance marketing consultant, performance marketing expert, paid media consultant, roas consultant, poas consultant',
    },
    schemaType: 'Service',
    hero: {
      kicker: 'Performance Marketing Consultant',
      h1: 'A Performance Marketing Consultant Who <span class="underline">actually runs the accounts</span>',
      sub: "I'm not an agency handing your ads to a junior. I'm the operator writing the sGTM containers, calibrating POAS bidding, and rebuilding attribution — so every rupee you spend maps to profit, not a dashboard illusion.",
      ctaPrimary: { label: 'Book a Strategy Call', href: '/#contact' },
      ctaSecondary: { label: 'See my methodology', href: '#pillars' },
      stats: [
        { num: '16+', label: 'Years in performance media' },
        { num: '9', label: 'Countries served' },
        { num: '$40M+', label: 'Ad spend managed' },
      ],
    },
    sections: [
      {
        type: 'prose',
        eyebrow: 'The Problem',
        h2: 'Your dashboards show ROAS. Your bank statement disagrees.',
        body: [
          "Most brands lose 25–40% of their paid-media budget to phantom conversions — signal loss from browser tracking, over-crediting inside Advantage+ and Performance Max, junk COD orders, and offline events that arrive weeks late. The dashboard looks green while the P&L bleeds.",
          "As a <strong>performance marketing consultant</strong>, my job is not to run more ads. It is to rebuild the data pipeline underneath your ads so the algorithm optimises toward <strong>margin, not volume</strong>. That means server-side tagging, deduplicated conversions, value-based bidding, and a media plan governed by MER — not last-click ROAS.",
          "Every engagement starts the same way: a technical paid-media audit. If the tracking is broken, no amount of creative or bid strategy will fix the burn rate. Fix the data first, then scale the media.",
        ],
      },
      {
        type: 'pillars',
        eyebrow: 'Methodology',
        h2: 'Four pillars I install for every client',
        anchor: 'pillars',
        items: [
          { num: '01', title: 'Signal Engineering', body: 'Server-side GTM, Meta CAPI, Google Enhanced Conversions and deduplicated event IDs. Restore true attribution before touching bids.' },
          { num: '02', title: 'Value-Based Bidding', body: 'Feed gross-margin (POAS), not revenue, to Google & Meta. The algorithm hunts profit, not deal-hunters or RTO-prone COD orders.' },
          { num: '03', title: 'MER-Governed Budgets', body: 'Cross-channel budgets rebalanced hourly against blended Marketing Efficiency Ratio — not siloed platform ROAS numbers that overlap.' },
          { num: '04', title: 'Creative & Landing Sprint', body: 'Iterate messaging and landing pages against qualified-lead quality, not top-of-funnel CTR. Volume without quality is a slow death.' },
        ],
      },
      {
        type: 'checklist',
        eyebrow: 'What you get',
        h2: 'What is inside a consulting engagement',
        items: [
          'Full paid-media audit across Google Ads, Meta, LinkedIn and programmatic',
          'sGTM container build (GCP or Stape) and Meta CAPI implementation',
          'Custom conversion schema mapped to your gross-margin data',
          'POAS / tCPA / tROAS bid strategy calibration',
          'MER-based cross-channel budget model with weekly rebalancing',
          'Creative brief system tied to funnel-stage-specific metrics',
          'Attribution model rebuild (GA4 + first-party warehouse)',
          'Weekly ops call + 24/7 async support on Slack/Loom',
        ],
      },
      {
        type: 'process',
        eyebrow: 'Engagement flow',
        h2: 'From audit to compounding growth in 90 days',
        steps: [
          { num: 'Week 1', title: 'Technical audit', body: 'Deep dive into every ad account, tracking layer, and CRM feedback loop. Starts with a diagnostic call.' },
          { num: 'Weeks 2–3', title: 'Infrastructure', body: 'Server-side tracking, CAPI, first-party warehouse, POAS schema. Fix the plumbing first.' },
          { num: 'Weeks 4–8', title: 'Rebuild & scale', body: 'Restructure campaigns, launch value-based bidding, refresh creative against qualified signals.' },
          { num: 'Weeks 9–12', title: 'Compound', body: 'MER budget governance, weekly experimentation, and a documented playbook your team owns.' },
        ],
      },
      {
        type: 'faq',
        eyebrow: 'FAQ',
        h2: 'What founders ask before hiring a performance marketing consultant',
        items: [
          { q: 'How is a consultant different from a paid-media agency?', a: 'You are hiring the person, not a pod of juniors. I sit inside your ad accounts, your GTM, and your data warehouse. No hand-offs. No account managers. No monthly retainer selling services you did not need.' },
          { q: 'Which platforms do you cover?', a: 'Google Ads (Search, PMax, Demand Gen), Meta (Advantage+, ASC, retention), LinkedIn Ads, Microsoft Ads, and programmatic via DV360 or The Trade Desk when scale justifies it.' },
          { q: 'Do you work with D2C, B2B, or both?', a: 'Both. Around 60% of my engagements are D2C (India + global), 40% are B2B SaaS. The frameworks are identical — the signal definitions and sales cycles differ.' },
          { q: 'What is the typical spend threshold for engagement?', a: 'I work best with brands spending $10K+ per month in paid media, or brands preparing to scale from that base. Below that, the audit alone often unlocks enough margin to fund the growth.' },
          { q: 'How long is the minimum engagement?', a: '90 days for a full rebuild. Some clients continue on a fractional retainer after that; some take the playbook in-house.' },
        ],
      },
    ],
    related: [
      { slug: 'paid-media-audit', title: 'Paid Media Audit', desc: 'One-off deep audit — the fastest way to see if your spend is efficient.' },
      { slug: 'server-side-tracking-consultant', title: 'Server-Side Tracking', desc: 'sGTM + CAPI implementation for brands losing signal to iOS/ITP.' },
      { slug: 'ga4-gtm-attribution-consultant', title: 'GA4 & GTM Attribution', desc: 'Rebuild attribution so your ROAS actually reflects reality.' },
    ],
  },

  'growth-marketing-consultant': {
    slug: 'growth-marketing-consultant',
    theme: { accent: '#14b8a6', accentSoft: 'rgba(20, 184, 166, 0.14)', accentStrong: '#0d9488' },
    heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1400&q=85&auto=format',
    meta: {
      title: 'Growth Marketing Consultant | Full-Funnel Growth for B2B & D2C',
      description: 'Growth Marketing Consultant Sanjib Roy — full-funnel acquisition, retention and lifecycle systems built on first-party data. 16+ years of operator experience for scaling brands.',
      keywords: 'growth marketing consultant, growth consultant, growth strategist, lifecycle marketing consultant, retention consultant',
    },
    schemaType: 'Service',
    hero: {
      kicker: 'Growth Marketing Consultant',
      h1: 'Growth is a <span class="underline">system</span>, not a channel.',
      sub: "A growth marketing consultant does not just buy more clicks. I build the compounding loop across acquisition, activation, retention and referral — so the second year of spend costs half as much as the first.",
      ctaPrimary: { label: 'Book a Growth Diagnostic', href: '/#contact' },
      ctaSecondary: { label: 'See the loop', href: '#pillars' },
      stats: [
        { num: '4.1x', label: 'Avg. LTV lift in 12 months' },
        { num: '−38%', label: 'Blended CAC (median)' },
        { num: '90-day', label: 'Framework to install' },
      ],
    },
    sections: [
      {
        type: 'prose',
        eyebrow: 'The Reframe',
        h2: 'Performance marketing spends. Growth marketing compounds.',
        body: [
          "Performance marketing asks: how do I get one more customer for less? <strong>Growth marketing</strong> asks: how do I make every customer worth twice as much, and turn them into the next channel? That distinction is why some brands stall at $1M ARR and others compound past $50M with the same paid budget.",
          "As your growth marketing consultant, I look at the full funnel — acquisition, activation, retention, monetisation, and referral — as one connected system. Weak retention makes paid unprofitable. Poor activation kills LTV. Bad onboarding starves the referral loop. Fix them individually and you get incrementalism. Fix them as a system and you get a compounding curve.",
          "The output is not a slide deck. It is a living <strong>growth model</strong>: a spreadsheet that predicts revenue, backed by cohort dashboards, tied to weekly experiments — and owned by your team long after the engagement ends.",
        ],
      },
      {
        type: 'pillars',
        eyebrow: 'The Loop',
        h2: 'Five compounding levers I install',
        anchor: 'pillars',
        items: [
          { num: '01', title: 'Acquisition Efficiency', body: 'Paid + organic + partnerships governed by blended MER, not siloed ROAS. The cheapest channel is often the one you are not running.' },
          { num: '02', title: 'Activation Rigor', body: 'A defined "aha moment" for every persona, instrumented and A/B tested. Activation lift is a leveraged multiplier on every downstream metric.' },
          { num: '03', title: 'Retention Engineering', body: 'Cohort analysis, churn prediction, and lifecycle triggers built on your product data. Every 10% retention lift doubles enterprise value.' },
          { num: '04', title: 'Monetisation Depth', body: 'Pricing tests, upsell paths, cross-sell automations, and premium tiers. Expansion revenue is the cheapest revenue.' },
          { num: '05', title: 'Referral Loops', body: 'Product-embedded referral, incentive design, and viral coefficients — the only channel that gets cheaper as you scale.' },
        ],
      },
      {
        type: 'checklist',
        eyebrow: 'Deliverables',
        h2: 'What you own after 12 weeks',
        items: [
          'Full growth model (acquisition × activation × retention × revenue) in Sheets or Notion',
          'North-star metric definition + weekly review ritual',
          'Cohort dashboard (GA4 + warehouse) with predictive LTV',
          'Lifecycle automation blueprint (email, SMS, WhatsApp, in-product)',
          'Experimentation backlog with hypothesis / cost / expected-lift for each',
          'Referral loop architecture and incentive math',
          'Content & SEO framework tied to buyer intent stages',
          'Team hiring plan for the roles I will not fill for you',
        ],
      },
      {
        type: 'process',
        eyebrow: 'Cadence',
        h2: 'A repeatable 90-day install',
        steps: [
          { num: 'Weeks 1–2', title: 'Diagnostic', body: 'Interviews, cohort review, funnel teardown, growth-model draft. You see the compounding gaps before we touch anything.' },
          { num: 'Weeks 3–5', title: 'Foundation', body: 'Instrumentation fixes, activation event definition, cohort dashboards, lifecycle automation build.' },
          { num: 'Weeks 6–10', title: 'Experimentation', body: 'Weekly high-velocity tests across acquisition, activation, retention. Learnings compound into the playbook.' },
          { num: 'Weeks 11–12', title: 'Handoff', body: 'Documented playbook, team training, hiring plan, 60-day follow-up cadence.' },
        ],
      },
      {
        type: 'faq',
        eyebrow: 'FAQ',
        h2: 'Growth consulting — the honest answers',
        items: [
          { q: 'Is growth marketing the same as performance marketing?', a: 'No. Performance marketing is a channel discipline (paid). Growth marketing is a systems discipline (full funnel). Most brands need both — but they are hired for different problems.' },
          { q: 'Do you take on early-stage startups?', a: 'Yes, if you have found product-market fit and are already generating revenue. Below PMF, no consultant can save the model. Above PMF, growth infrastructure is the highest-ROI investment you can make.' },
          { q: 'Will you run our ad accounts?', a: 'Yes, if the engagement requires it. I do not outsource execution — you get the operator.' },
          { q: 'Do you replace our in-house marketing team?', a: 'The opposite. I install systems your team runs after I leave. Fractional CMO by outcome, not by hours.' },
          { q: 'What if we are pre-Series-A?', a: 'The 12-week engagement gives you a growth model most seed-stage founders never build. Investors notice.' },
        ],
      },
    ],
    related: [
      { slug: 'performance-marketing-consultant', title: 'Performance Marketing', desc: 'Paid-media execution: the acquisition engine inside the growth loop.' },
      { slug: 'conversion-rate-optimization-consultant', title: 'CRO Consulting', desc: 'Activation and monetisation depth — the multiplier on your funnel.' },
      { slug: 'ai-marketing-consulting', title: 'AI Marketing', desc: 'Predictive LTV, automated personalisation, and agentic AI for growth teams.' },
    ],
  },

  'paid-media-audit': {
    slug: 'paid-media-audit',
    theme: { accent: '#f97316', accentSoft: 'rgba(249, 115, 22, 0.16)', accentStrong: '#ea580c' },
    heroImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1400&q=85&auto=format',
    meta: {
      title: 'Paid Media Audit — Find the Leak Before You Scale | Sanjib Roy',
      description: 'A rigorous, forensic paid media audit across Google Ads, Meta, LinkedIn and programmatic. Uncover budget waste, tracking gaps and mis-attributed conversions in 10 business days.',
      keywords: 'paid media audit, google ads audit, meta ads audit, ppc audit, tracking audit',
    },
    schemaType: 'Service',
    hero: {
      kicker: 'Paid Media Audit',
      h1: 'A forensic audit that finds <span class="underline">where the budget is bleeding</span>',
      sub: "Not a template. Not a Semrush export. A 40-page teardown of your ad accounts, tracking layer, and attribution model — delivered in 10 business days by the operator you would otherwise hire full-time.",
      ctaPrimary: { label: 'Request the Audit', href: '/#contact' },
      ctaSecondary: { label: 'What is included', href: '#pillars' },
      stats: [
        { num: '10 days', label: 'From kickoff to delivery' },
        { num: '25–40%', label: 'Waste typically uncovered' },
        { num: '40+', label: 'Diagnostic checks' },
      ],
    },
    sections: [
      {
        type: 'prose',
        eyebrow: 'Why audit first',
        h2: 'Scaling a broken account only scales the leak.',
        body: [
          "Every paid-media conversation should start with a <strong>forensic audit</strong> — not a fresh campaign build. Because if your Meta pixel is de-duplicating against a broken event_id, if your GA4 is losing 30% of sessions, or if PMax is claiming search-branded conversions, then more budget just amplifies the waste.",
          "This audit is not a checklist ChatGPT could produce. It is 40+ hand-run diagnostics across seven layers: account structure, bidding, creative velocity, tracking integrity, attribution, feed hygiene, and offline signal quality. Each finding is tagged by severity, dollar impact, and time-to-fix.",
          "You get the raw report, a 60-minute walkthrough, and a prioritised remediation roadmap. Whether you fix it yourself, use your agency, or hire me to execute — you leave the audit knowing exactly where every rupee is going.",
        ],
      },
      {
        type: 'pillars',
        eyebrow: 'Coverage',
        h2: 'Seven layers audited',
        anchor: 'pillars',
        items: [
          { num: '01', title: 'Account Architecture', body: 'Campaign structure, negatives, budget allocation, keyword sculpting, exclusion logic. The bones must be right.' },
          { num: '02', title: 'Bidding Strategy', body: 'tCPA vs tROAS vs Max Conversions vs Max Value — which is right for which campaign, and where you are leaving margin on the table.' },
          { num: '03', title: 'Tracking Integrity', body: 'Pixel/tag firing, event schema, deduplication, event_ids, GA4 measurement protocol, CAPI health.' },
          { num: '04', title: 'Attribution Model', body: 'GA4 model, cross-device stitching, first-touch vs data-driven, offline conversion imports.' },
          { num: '05', title: 'Creative Velocity', body: 'Fatigue curves, creative library size, hook/body/CTA analysis, static vs UGC vs video mix.' },
          { num: '06', title: 'Feed & Shopping', body: 'Merchant Center health, feed enrichment, title/attribute optimisation, disapproval clean-up.' },
          { num: '07', title: 'Offline Signals', body: 'CRM sync latency, lead-quality scoring, POAS calculation integrity, RTO handling for COD markets.' },
        ],
      },
      {
        type: 'checklist',
        eyebrow: 'Deliverables',
        h2: 'What lands in your inbox',
        items: [
          '40-page written audit report (PDF + Notion)',
          'Loom walkthrough for every critical finding',
          'Prioritised remediation roadmap with dollar-impact estimates',
          'Tracking schema documentation (current state + target state)',
          'GA4 exploration + BigQuery starter queries',
          'Creative library scoring rubric with named winners',
          '60-minute live walkthrough with your team',
          '30-day post-delivery Q&A window (async Slack/email)',
        ],
      },
      {
        type: 'process',
        eyebrow: 'Timeline',
        h2: 'Ten business days, start to walkthrough',
        steps: [
          { num: 'Day 1', title: 'Kickoff', body: '60-min call. Access provisioning. Business context capture. NDA if needed.' },
          { num: 'Days 2–6', title: 'Deep dive', body: 'All seven layers audited. Each finding logged, evidenced with screenshots, scored for severity and impact.' },
          { num: 'Days 7–9', title: 'Synthesis', body: 'Roadmap built, dollar impacts modelled, walkthrough deck assembled.' },
          { num: 'Day 10', title: 'Delivery', body: 'Report + Loom + live walkthrough. 30 days of async Q&A included.' },
        ],
      },
      {
        type: 'faq',
        eyebrow: 'FAQ',
        h2: 'Common questions before commissioning an audit',
        items: [
          { q: 'Do you audit accounts run by other agencies?', a: 'Yes — and roughly 70% of audits I run are on accounts still with their current agency. My job is a second opinion, not a takedown.' },
          { q: 'What access do you need?', a: 'Reader access to Google Ads, Meta Business Manager, GA4, Google Tag Manager, Search Console, and Shopify / your CRM. No admin access, no login sharing.' },
          { q: 'Will you sign an NDA?', a: 'Yes, always, on request. Every engagement is confidential by default.' },
          { q: 'What if there is nothing to fix?', a: 'That has never happened. In accounts spending $50k+/month, every audit has found at least 5-figures of annualised waste.' },
          { q: 'Can you also execute the remediation?', a: 'Yes — that is the natural next step. Many audit clients continue into a 90-day performance marketing consulting engagement.' },
        ],
      },
    ],
    related: [
      { slug: 'performance-marketing-consultant', title: 'Performance Consulting', desc: 'Post-audit execution: rebuild media around the findings.' },
      { slug: 'ga4-gtm-attribution-consultant', title: 'GA4 & GTM', desc: 'The attribution layer we usually rebuild after an audit.' },
      { slug: 'server-side-tracking-consultant', title: 'Server-Side Tracking', desc: 'Fix the tracking-integrity findings with sGTM + CAPI.' },
    ],
  },

  'ga4-gtm-attribution-consultant': {
    slug: 'ga4-gtm-attribution-consultant',
    theme: { accent: '#a855f7', accentSoft: 'rgba(168, 85, 247, 0.16)', accentStrong: '#9333ea' },
    heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1400&q=85&auto=format',
    meta: {
      title: 'GA4, GTM & Attribution Consultant | Sanjib Roy',
      description: 'Certified GA4 / GTM / attribution consultant. Rebuild your measurement stack — event schema, custom dimensions, BigQuery pipelines, data-driven attribution — so the numbers you report are the numbers that are real.',
      keywords: 'ga4 consultant, gtm consultant, google analytics 4, attribution consultant, bigquery marketing',
    },
    schemaType: 'Service',
    hero: {
      kicker: 'GA4 / GTM / Attribution',
      h1: 'GA4 is only powerful if <span class="underline">the schema is right</span>.',
      sub: "Most brands ported UA to GA4 in a hurry and never rebuilt the event schema. That is why nothing matches Google Ads, cohort reports look wrong, and BigQuery data is unusable. I fix all three, in the same engagement.",
      ctaPrimary: { label: 'Rebuild My Analytics', href: '/#contact' },
      ctaSecondary: { label: 'How I rebuild it', href: '#pillars' },
      stats: [
        { num: '100%', label: 'Google Ads / GA4 reconciliation' },
        { num: 'BigQuery', label: 'Native pipelines' },
        { num: 'DDA', label: 'Data-driven attribution' },
      ],
    },
    sections: [
      {
        type: 'prose',
        eyebrow: 'The Situation',
        h2: 'If your GA4 does not match Google Ads, everything downstream is guesswork.',
        body: [
          "Universal Analytics forgave sloppy implementation. <strong>GA4 does not.</strong> Event-based schema, consent-mode signals, enhanced conversions, and BigQuery export are all interdependent. Get one wrong and every dashboard, every attribution report, every retargeting audience gets slightly off — and slightly off compounds.",
          "As a GA4 / GTM / attribution consultant, I rebuild the entire measurement stack: event taxonomy, custom dimensions, user-ID unification, consent mode v2, server-side GTM, Enhanced Conversions, BigQuery streaming export, and a Looker Studio (or Sigma / Mode) layer that finance can trust.",
          "The goal is not \"more data\". The goal is <strong>reliable, actionable, executive-grade reporting</strong> where paid-media claims, product usage, and finance revenue reconcile within 2% every week.",
        ],
      },
      {
        type: 'pillars',
        eyebrow: 'The Rebuild',
        h2: 'Six layers, one integrated stack',
        anchor: 'pillars',
        items: [
          { num: '01', title: 'Event Taxonomy', body: 'A documented event schema (naming, parameters, business definitions) versioned in Notion — the single source of truth for engineering + marketing.' },
          { num: '02', title: 'GTM Web + Server', body: 'Web GTM restructured. Server-side GTM (GCP or Stape) deployed. All ad platform tags migrated for signal recovery.' },
          { num: '03', title: 'Enhanced Conversions', body: 'User-provided data hashed (SHA-256) and forwarded to Google Ads and Meta CAPI for match-rate restoration.' },
          { num: '04', title: 'GA4 Configuration', body: 'Custom dimensions, custom metrics, conversion events, audiences, and exploration templates — all mapped to your growth model.' },
          { num: '05', title: 'BigQuery Export', body: 'Streaming export activated. Warehouse tables partitioned. Base queries and views built for cohort / LTV / attribution analysis.' },
          { num: '06', title: 'Attribution & Reporting', body: 'Data-driven attribution enabled, model comparison built, custom Looker Studio suite handed over.' },
        ],
      },
      {
        type: 'checklist',
        eyebrow: 'Included',
        h2: 'What is in the engagement',
        items: [
          'Full audit of current GA4, GTM, ad-platform tags',
          'Documented event & parameter schema (v1.0 baseline)',
          'GTM web + server container rebuild',
          'Consent mode v2 implementation (India + EU-safe)',
          'Enhanced Conversions for Google and Meta CAPI',
          'BigQuery export + starter query pack (cohort, LTV, attribution)',
          'Looker Studio executive dashboard (customised)',
          'Team training + video documentation library',
        ],
      },
      {
        type: 'process',
        eyebrow: 'Timeline',
        h2: 'Six-week rebuild, 100% reconciled by week 8',
        steps: [
          { num: 'Weeks 1–2', title: 'Audit + schema', body: 'Current-state audit. Draft the event schema together with your product and marketing teams.' },
          { num: 'Weeks 3–4', title: 'Build', body: 'GTM web + server rebuild. Enhanced Conversions and CAPI wired. Consent mode deployed.' },
          { num: 'Week 5', title: 'BigQuery + reporting', body: 'Warehouse export live. Base queries + Looker Studio dashboards.' },
          { num: 'Week 6', title: 'Reconcile + train', body: 'Numbers reconciled across GA4, Google Ads, Meta, revenue. Handoff.' },
        ],
      },
      {
        type: 'faq',
        eyebrow: 'FAQ',
        h2: 'GA4 & attribution questions I get every week',
        items: [
          { q: 'Do I need server-side GTM?', a: 'If you depend on Meta or Google conversion signals at any meaningful volume — yes. iOS ITP and browser extensions are eating 15–35% of your signal. sGTM is the only structural fix.' },
          { q: 'Can you migrate from Universal Analytics if I never did?', a: 'Yes. Even in 2026 I see brands still on shadow UA workflows. Full migration is part of the engagement.' },
          { q: 'Is BigQuery worth it for a small brand?', a: 'If you have >100 transactions a day or a multi-touch B2B funnel, absolutely. BigQuery costs a few dollars a month and unlocks LTV, cohort, and attribution analysis GA4 UI cannot do.' },
          { q: 'What is data-driven attribution?', a: 'A machine-learning model that credits conversions across touchpoints based on their actual incremental contribution — not last-click. It requires ~600 conversions and 30 days of data in GA4.' },
          { q: 'Do you support Piwik Pro, Matomo, Segment, or Rudderstack?', a: 'Yes — I have implemented all four. Analytics platform choice is negotiable; the schema discipline is not.' },
        ],
      },
    ],
    related: [
      { slug: 'server-side-tracking-consultant', title: 'Server-Side Tracking', desc: 'The sGTM + CAPI layer that makes GA4 signal reliable again.' },
      { slug: 'paid-media-audit', title: 'Paid Media Audit', desc: 'Attribution gaps found via audit often trigger the full GA4 rebuild.' },
      { slug: 'performance-marketing-consultant', title: 'Performance Consulting', desc: 'Attribution is only worth fixing if you also fix the media buying.' },
    ],
  },

  'server-side-tracking-consultant': {
    slug: 'server-side-tracking-consultant',
    theme: { accent: '#38bdf8', accentSoft: 'rgba(56, 189, 248, 0.15)', accentStrong: '#0ea5e9' },
    heroImage: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1400&q=85&auto=format',
    meta: {
      title: 'Server-Side Tracking Consultant | sGTM, CAPI & Signal Recovery',
      description: 'Server-Side Tracking Consultant — build server-side GTM on GCP or Stape, activate Meta CAPI, Google Enhanced Conversions and TikTok Events API. Restore signal fidelity lost to iOS/ITP.',
      keywords: 'server-side tracking consultant, sgtm consultant, meta capi consultant, google conversions api, stape.io consultant',
    },
    schemaType: 'Service',
    hero: {
      kicker: 'Server-Side Tracking',
      h1: 'Get back the <span class="underline">30% of ad signal</span> the browser is quietly killing.',
      sub: "iOS 17+ / ITP, Chrome sandbox, ad blockers, and consent-mode gating are silently deleting your Meta pixel fires and Google enhanced conversions. Server-side GTM + CAPI is not optional in 2026. It is the only way to survive.",
      ctaPrimary: { label: 'Book an Implementation Call', href: '/#contact' },
      ctaSecondary: { label: 'How I deploy it', href: '#pillars' },
      stats: [
        { num: '+27%', label: 'Median match-rate lift' },
        { num: 'GCP / Stape', label: 'Two deployment paths' },
        { num: '6-week', label: 'Full production install' },
      ],
    },
    sections: [
      {
        type: 'prose',
        eyebrow: 'The Reality',
        h2: 'Your Meta pixel is lying to you. Politely, but consistently.',
        body: [
          "Since iOS 17, Safari's Intelligent Tracking Prevention actively deletes third-party pixel data after 24 hours. Chrome's Privacy Sandbox is closing the same gap. Meanwhile, ad blockers strip your Facebook pixel outright on 12–20% of Indian traffic.",
          "The only durable answer is <strong>server-side tracking</strong>. Events are collected in the browser, forwarded to a server container (Google Cloud Run or Stape), enriched with first-party data, hashed for privacy, and pushed directly to Meta Conversions API, Google Enhanced Conversions, TikTok Events API, Snap CAPI, LinkedIn CAPI — bypassing the browser entirely.",
          "This is a real engineering project, not a plugin. Done right, it restores <strong>25–40% of lost signal</strong>, doubles Meta's Event Match Quality, and unlocks POAS bidding. Done wrong, it duplicates events, breaks your attribution, and burns cloud budget. I have shipped this build over 30 times.",
        ],
      },
      {
        type: 'pillars',
        eyebrow: 'Stack',
        h2: 'The four decisions that make or break the build',
        anchor: 'pillars',
        items: [
          { num: '01', title: 'GCP vs Stape', body: 'Google Cloud Run gives full control and lowest cost at scale. Stape gives fastest deployment and managed logs. I pick based on your engineering bandwidth.' },
          { num: '02', title: 'Event Dedup', body: 'Every event needs a shared event_id across browser and server so Meta CAPI can deduplicate. Without this, you are double-counting.' },
          { num: '03', title: 'First-Party Enrichment', body: 'The server container is where you add hashed email, phone, LTV score, and margin — the signal Meta and Google actually reward.' },
          { num: '04', title: 'Consent Mode v2', body: 'Consent mode v2 is now mandatory for EU. The server container is where consent state is respected before forwarding. Non-negotiable.' },
        ],
      },
      {
        type: 'checklist',
        eyebrow: 'Deliverables',
        h2: 'What ships to production',
        items: [
          'Server container (GCP Cloud Run or Stape)',
          'Custom domain (tracking.yourbrand.com) with SSL',
          'Web GTM restructured for parallel client + server firing',
          'Meta CAPI with event_id deduplication + EMQ ≥ 8.0',
          'Google Enhanced Conversions and Ads server forwarding',
          'TikTok Events API, LinkedIn Insight Tag CAPI, Snap CAPI (if used)',
          'Consent Mode v2 wired end-to-end',
          'Runbook + team training + 30-day support window',
        ],
      },
      {
        type: 'process',
        eyebrow: 'Timeline',
        h2: 'Six-week production install',
        steps: [
          { num: 'Weeks 1–2', title: 'Design', body: 'Event schema, GCP or Stape decision, domain planning, consent policy review.' },
          { num: 'Weeks 3–4', title: 'Build', body: 'Container deployed. Client and server GTM restructured. Ad-platform tags migrated. Deduplication validated.' },
          { num: 'Week 5', title: 'Validate', body: 'Meta EMQ tuning, Google enhanced-conversion coverage, dashboards for signal parity.' },
          { num: 'Week 6', title: 'Handoff', body: 'Runbook, monitoring alerts, team training. 30-day support window active.' },
        ],
      },
      {
        type: 'faq',
        eyebrow: 'FAQ',
        h2: 'Server-side tracking — common questions',
        items: [
          { q: 'How much does sGTM cost to run?', a: 'On GCP Cloud Run: $30–$120/month for most mid-market brands. On Stape: $99–$299/month for managed. This is a rounding error against the paid-media it protects.' },
          { q: 'Will this break existing tracking?', a: 'No. We run web pixels and server events in parallel with deduplication for two weeks before deprecating the client-only path.' },
          { q: 'Do I need a developer on our side?', a: 'Ideally, yes — for DNS and Cloud Run permissions. But I have shipped installs for brands with zero engineering by using Stape.' },
          { q: 'What about privacy compliance?', a: 'Server-side tracking is more privacy-safe, not less. You control what forwards, honour consent mode, hash PII, and stay compliant with GDPR / DPDP.' },
          { q: 'Which ad platforms benefit most?', a: 'Meta (largest signal recovery), TikTok (huge in India / SEA), Google Ads (Enhanced Conversions), LinkedIn B2B (CAPI). All four run through the same container.' },
        ],
      },
    ],
    related: [
      { slug: 'ga4-gtm-attribution-consultant', title: 'GA4 & Attribution', desc: 'sGTM is the plumbing; GA4 is the reporting. Usually done together.' },
      { slug: 'paid-media-audit', title: 'Paid Media Audit', desc: 'The audit that quantifies exactly how much signal you are losing.' },
      { slug: 'performance-marketing-consultant', title: 'Performance Marketing', desc: 'Once signal is restored, media buying can actually scale profitably.' },
    ],
  },

  'conversion-rate-optimization-consultant': {
    slug: 'conversion-rate-optimization-consultant',
    theme: { accent: '#eab308', accentSoft: 'rgba(234, 179, 8, 0.16)', accentStrong: '#ca8a04' },
    heroImage: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1400&q=85&auto=format',
    meta: {
      title: 'Conversion Rate Optimization Consultant | CRO for D2C & SaaS',
      description: 'CRO Consultant Sanjib Roy — full-funnel conversion rate optimization for D2C and SaaS. Analytics-first, hypothesis-led experimentation with real statistical rigour.',
      keywords: 'cro consultant, conversion rate optimization consultant, cro audit, ab testing consultant, funnel optimization',
    },
    schemaType: 'Service',
    hero: {
      kicker: 'Conversion Rate Optimization',
      h1: 'Doubling conversion rate <span class="underline">halves your CAC</span> without adding a rupee of spend.',
      sub: "Most CRO agencies A/B test button colours. I audit the whole funnel — search intent, landing message, form friction, checkout drop-off, activation triggers — and run experiments backed by real statistical significance.",
      ctaPrimary: { label: 'Book a CRO Diagnostic', href: '/#contact' },
      ctaSecondary: { label: 'The framework', href: '#pillars' },
      stats: [
        { num: '+42%', label: 'Median conversion lift (90d)' },
        { num: 'p < 0.05', label: 'Every winning test' },
        { num: '3–5', label: 'Experiments per week' },
      ],
    },
    sections: [
      {
        type: 'prose',
        eyebrow: 'The Discipline',
        h2: 'CRO is not tricks. It is systematic hypothesis testing.',
        body: [
          "There is an entire industry of \"CRO experts\" selling colour changes and pop-up templates. Real conversion rate optimisation is a <strong>research-heavy, statistically rigorous discipline</strong>. It looks like a scientific method, because it is one: question → hypothesis → test → measure → decide → document.",
          "As a CRO consultant, I start every engagement with a full funnel diagnostic — session replays, heatmaps, form analytics, cart flow analysis, and cohort retention. From that we build a prioritised experimentation backlog. Every test has a written hypothesis, a minimum detectable effect, a sample size calculation, and a stop rule.",
          "The result: 3–5 tests per week, roughly one-third winners, learnings compounding into a playbook your team can run forever. Every winning experiment is compounding leverage on <strong>every future ad dollar</strong>.",
        ],
      },
      {
        type: 'pillars',
        eyebrow: 'Framework',
        h2: 'Four layers of the funnel, tested in order',
        anchor: 'pillars',
        items: [
          { num: '01', title: 'Ad-to-Landing Match', body: 'Message continuity from ad creative to landing page hero. If the promise breaks, no downstream test can save the funnel.' },
          { num: '02', title: 'Page Experience', body: 'Above-fold clarity, social proof placement, objection handling, sticky CTAs, Core Web Vitals. The 90% of visitors who never scroll below the fold.' },
          { num: '03', title: 'Form / Cart Friction', body: 'Field count, error handling, guest checkout, wallet-first UX, address autofill, RTO risk flags for COD.' },
          { num: '04', title: 'Activation & Retention', body: 'Post-conversion onboarding, second-order triggers, lifecycle sequences — the CRO most brands never think of.' },
        ],
      },
      {
        type: 'checklist',
        eyebrow: 'What is included',
        h2: 'Engagement deliverables',
        items: [
          'Full-funnel CRO diagnostic (session replay, heatmap, form analytics)',
          'Prioritised experimentation backlog (ICE-scored)',
          'Test spec template + statistical rigour framework',
          'A/B testing tool selection & setup (VWO / Convert / Optimize alternative)',
          '3–5 experiments per week execution',
          'Weekly review with winning-loser learnings',
          'Living playbook of what works on your traffic',
          'Team training so CRO continues after the engagement',
        ],
      },
      {
        type: 'process',
        eyebrow: 'Cadence',
        h2: 'Twelve weeks. Compounding lift.',
        steps: [
          { num: 'Weeks 1–2', title: 'Diagnostic', body: 'Full funnel research: analytics, session replay, form logs, user interviews. Backlog built.' },
          { num: 'Weeks 3–4', title: 'Foundation', body: 'Testing tool live, tracking wired, first three experiments live.' },
          { num: 'Weeks 5–10', title: 'Experimentation', body: '3–5 tests per week, weekly review, backlog refreshed against learnings.' },
          { num: 'Weeks 11–12', title: 'Playbook', body: 'Documented what works, testing framework handed over, roadmap for next 90 days.' },
        ],
      },
      {
        type: 'faq',
        eyebrow: 'FAQ',
        h2: 'CRO questions founders ask',
        items: [
          { q: 'How much traffic do I need for CRO?', a: '1,000+ conversions/month unlocks fast A/B testing. Below that we focus on qualitative research + user-tested changes rather than statistical A/B tests.' },
          { q: 'What testing tool do you recommend?', a: 'VWO or Convert for most brands. Optimize is deprecated. If you have engineering bandwidth, GrowthBook or Statsig with server-side experiments.' },
          { q: 'Will you optimise the whole site or just landing pages?', a: 'Whole site. Landing pages, PDPs, checkout, onboarding, lifecycle emails — CRO is a funnel discipline, not a page discipline.' },
          { q: 'How does CRO relate to performance marketing?', a: 'CRO doubles the leverage on every ad dollar. Running paid without CRO is like scaling a leaky bucket.' },
          { q: 'Do you handle copy, design, and dev of variants?', a: 'Yes. Every experiment includes copy, design, dev, QA, and launch. Your team reviews and approves — I own execution.' },
        ],
      },
    ],
    related: [
      { slug: 'growth-marketing-consultant', title: 'Growth Consulting', desc: 'CRO is one lever inside the full growth loop.' },
      { slug: 'performance-marketing-consultant', title: 'Performance Marketing', desc: 'The paid channels CRO makes profitable.' },
      { slug: 'ga4-gtm-attribution-consultant', title: 'GA4 & Attribution', desc: 'Reliable measurement is prerequisite to statistically-valid CRO.' },
    ],
  },

  'ai-marketing-consulting': {
    slug: 'ai-marketing-consulting',
    theme: { accent: '#8b5cf6', accentSoft: 'rgba(139, 92, 246, 0.16)', accentStrong: '#7c3aed' },
    heroImage: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1400&q=85&auto=format',
    meta: {
      title: 'AI Marketing Consulting | Agentic AI, Predictive LTV, Automated Personalisation',
      description: 'AI Marketing Consultant Sanjib Roy — deploy agentic AI, predictive LTV models, automated personalisation and creative-at-scale workflows built on your first-party data.',
      keywords: 'ai marketing consulting, agentic ai marketing, ai marketing consultant, predictive analytics, generative ai marketing',
    },
    schemaType: 'Service',
    hero: {
      kicker: 'AI Marketing Consulting',
      h1: 'Agentic AI, built on <span class="underline">your data</span> — not off-the-shelf gimmicks.',
      sub: "AI marketing is not ChatGPT prompts and a blog schedule. It is autonomous agents that pull margin from your warehouse, throttle Meta budgets in real time, and personalise every lifecycle touch. That is what I build.",
      ctaPrimary: { label: 'Book an AI Roadmap Call', href: '/#contact' },
      ctaSecondary: { label: 'What I actually build', href: '#pillars' },
      stats: [
        { num: '3–5', label: 'AI agents live per client' },
        { num: '−41%', label: 'Time spent on ops (median)' },
        { num: '2026', label: 'Playbook, not 2022' },
      ],
    },
    sections: [
      {
        type: 'prose',
        eyebrow: 'The Distinction',
        h2: 'Platform ML is optimising your budget away. Agentic AI defends it.',
        body: [
          "Every ad platform's <strong>internal ML</strong> — Google Performance Max, Meta Advantage+ — is designed to spend your budget efficiently for the platform. Not for you. Its blind spot is your gross margin, your inventory position, your RTO risk, your product-market fit signal.",
          "<strong>Agentic AI marketing</strong> lives outside the platform. It reads from your data warehouse, your CRM, your finance system — and takes autonomous action: pausing campaigns when margin drops, boosting bids on high-LTV cohorts, generating creative from top-performing angles, personalising every lifecycle touchpoint.",
          "I build these agents on real infrastructure: OpenAI / Anthropic models via APIs, LangGraph for orchestration, your warehouse as the source of truth, and human-in-the-loop guardrails for anything that spends money.",
        ],
      },
      {
        type: 'pillars',
        eyebrow: 'Deployments',
        h2: 'Five agent archetypes I typically deploy',
        anchor: 'pillars',
        items: [
          { num: '01', title: 'Budget Sentinel', body: 'Autonomously monitors blended MER hourly, throttles under-performing campaigns, boosts winners — against your business rules, not the platform\'s.' },
          { num: '02', title: 'Creative Analyst', body: 'Reads your ad library, scores creatives against actual funnel metrics, drafts new variants using generative tools, and briefs your designer.' },
          { num: '03', title: 'LTV Predictor', body: 'A predictive model that scores every new customer within their first session — feeding value-based bidding, cohort segmentation, and lifecycle triggers.' },
          { num: '04', title: 'Personalisation Engine', body: 'Dynamic on-site content, hero copy, product recommendations, and email/SMS variants tuned to predicted LTV and behavioural intent.' },
          { num: '05', title: 'Attribution Reconciler', body: 'Nightly reconciliation across GA4, ad platforms, warehouse, and finance. Flags anomalies before your Monday report does.' },
        ],
      },
      {
        type: 'checklist',
        eyebrow: 'Scope',
        h2: 'What an AI marketing engagement includes',
        items: [
          'AI opportunity audit across your growth stack',
          'Agent architecture design (which agents, where they run, guardrails)',
          'Infrastructure setup (warehouse, orchestration, LLM APIs)',
          'Predictive LTV model trained on your cohort data',
          'Generative creative pipeline (briefs → drafts → approvals)',
          'Personalisation infrastructure (email, on-site, ad audiences)',
          'Human-in-the-loop governance for spend-related agents',
          'Team training + agent playbook documentation',
        ],
      },
      {
        type: 'process',
        eyebrow: 'Timeline',
        h2: 'Ninety days from proposal to production agents',
        steps: [
          { num: 'Weeks 1–2', title: 'AI audit', body: 'Data maturity check. Agent shortlist. Business-rule capture. Guardrail policy design.' },
          { num: 'Weeks 3–6', title: 'Infrastructure', body: 'Warehouse hardening, LTV model training, first agent (Budget Sentinel or LTV Predictor) live.' },
          { num: 'Weeks 7–10', title: 'Scale', body: 'Remaining agents deployed. Personalisation engine plugged in. Weekly retros.' },
          { num: 'Weeks 11–12', title: 'Handoff', body: 'Playbook, documentation, team training. Agent maintenance retainer optional.' },
        ],
      },
      {
        type: 'faq',
        eyebrow: 'FAQ',
        h2: 'AI marketing — questions I get every week',
        items: [
          { q: 'Is this just ChatGPT for marketing?', a: 'No. LLM chat is a tool, not a strategy. Agentic AI marketing is autonomous software that reads your data, takes action, and learns — with humans in the loop for spend decisions.' },
          { q: 'Do you replace my current stack?', a: 'Rarely. Most engagements augment existing tools (Klaviyo, Braze, Segment, your ad platforms) with agent orchestration on top.' },
          { q: 'Which LLMs do you use?', a: 'Model-agnostic. OpenAI, Anthropic, Google Gemini, Emergent LLM key — chosen per task by capability and cost. Vendor lock-in is a bug.' },
          { q: 'What if the agents make a mistake?', a: 'Every agent with spend authority runs with hard guardrails, budget caps, and a human approval loop for anything above threshold. Autonomy is calibrated, not blind.' },
          { q: 'What data do you need?', a: 'Ad-platform data, GA4 or product analytics, CRM, revenue system. If you have a warehouse (BigQuery, Snowflake, Databricks), we go faster.' },
        ],
      },
    ],
    related: [
      { slug: 'performance-marketing-consultant', title: 'Performance Marketing', desc: 'The paid-media accounts your agents will govern.' },
      { slug: 'growth-marketing-consultant', title: 'Growth Consulting', desc: 'The systems view your agents operate inside.' },
      { slug: 'ga4-gtm-attribution-consultant', title: 'GA4 & Attribution', desc: 'The data layer that makes AI agents possible.' },
    ],
  },

  'performance-marketing-consultant-kolkata': {
    slug: 'performance-marketing-consultant-kolkata',
    theme: { accent: '#f43f5e', accentSoft: 'rgba(244, 63, 94, 0.14)', accentStrong: '#e11d48' },
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/7617b4cd-8fa4-4ce4-bfff-2f2453604cea/images/c93c96ede7d30b73f12652ce02bd42833c85d98042b2890f2814bb4ae0253f4c.jpeg',
    meta: {
      title: 'Performance Marketing Consultant in Kolkata | Sanjib Roy',
      description: 'Kolkata-based Performance Marketing Consultant with 16+ years of global experience. Google Ads, Meta, sGTM, POAS for D2C and B2B brands across India and worldwide.',
      keywords: 'performance marketing consultant kolkata, digital marketing consultant kolkata, google ads expert kolkata, meta ads consultant kolkata, paid media consultant india',
    },
    schemaType: 'LocalBusiness',
    hero: {
      kicker: 'Based in Kolkata · Working Globally',
      h1: 'A Performance Marketing Consultant in <span class="underline">Kolkata</span>, ranked by results',
      sub: "Sixteen years of hands-on ad operations across India, US, UK, Canada, UAE and SEA. Kolkata-based. Available on-site across the city and remote worldwide. I do not manage a team — you get the operator.",
      ctaPrimary: { label: 'Book a Local Meeting', href: '/#contact' },
      ctaSecondary: { label: 'How I work locally', href: '#pillars' },
      stats: [
        { num: '16+', label: 'Years, based in Kolkata' },
        { num: 'On-site', label: 'Across Salt Lake / New Town' },
        { num: '9', label: 'Countries served remotely' },
      ],
    },
    sections: [
      {
        type: 'prose',
        eyebrow: 'Local + Global',
        h2: 'Kolkata operator. Global playbook.',
        body: [
          "Kolkata's D2C and B2B scene has grown fast — Shyambazar founders selling to the US, Rajarhat SaaS teams targeting Southeast Asia, agencies scaling regional retail brands. What has not kept pace is the local consulting depth in performance marketing.",
          "I am a <strong>Kolkata-based performance marketing consultant</strong> who has spent 16 years running paid media across nine countries. I sit in New Town, meet clients across Salt Lake, Park Street and Rajarhat, and travel to Bangalore, Delhi and Bombay every month. The bench of Google Ads Search, Meta Advantage+, LinkedIn ABM, sGTM, POAS bidding and predictive LTV modelling is the same whether the client is in Kolkata or Kuala Lumpur.",
          "If you are a founder looking for a senior performance marketing consultant who can meet in person, get inside your ad accounts by evening, and hand you a global-tier playbook — this is the shortlist of one.",
        ],
      },
      {
        type: 'pillars',
        eyebrow: 'Why local matters',
        h2: 'Four reasons Kolkata brands choose a local consultant',
        anchor: 'pillars',
        items: [
          { num: '01', title: 'In-person cadence', body: 'Weekly on-site reviews at your office. Whiteboard sessions. Real-time team training. Remote is fine — but proximity accelerates trust.' },
          { num: '02', title: 'Local market intel', body: 'RTO patterns, COD handling, regional creative preferences, festival calendar planning — the nuances Bangalore agencies miss.' },
          { num: '03', title: 'Capability that stays', body: 'On-site work includes your team, not just your accounts. Weekly reviews double as training. When the engagement ends the capability sits in your building — not on a Bangalore retainer.' },
          { num: '04', title: 'Same day access', body: 'When your account breaks at 10 AM, I am available by noon. That is a promise no US or Bangalore consultant can match.' },
        ],
      },
      {
        type: 'checklist',
        eyebrow: 'Services offered locally',
        h2: 'What I deliver for Kolkata brands',
        items: [
          'Google Ads, Meta, LinkedIn strategy and daily operations',
          'sGTM (Google Cloud or Stape) implementation',
          'GA4 rebuild with BigQuery + Looker Studio dashboards',
          'POAS / value-based bidding calibration',
          'Full-funnel CRO and landing page optimisation',
          'AI marketing pilot design (predictive LTV, agentic budget)',
          'Team training and hiring roadmap for in-house growth teams',
          'Quarterly on-site strategy offsite (Kolkata / travel)',
        ],
      },
      {
        type: 'process',
        eyebrow: 'How we start',
        h2: 'From coffee in Park Street to production in 45 days',
        steps: [
          { num: 'Week 0', title: 'Intro coffee', body: 'A 60-minute meeting anywhere in Kolkata to understand your business, scope and challenges. Zero commitment.' },
          { num: 'Weeks 1–2', title: 'On-site audit', body: 'On-site kickoff, access provisioning, audit deliverables, roadmap presentation to your leadership.' },
          { num: 'Weeks 3–6', title: 'Implementation', body: 'Weekly on-site reviews. sGTM, GA4, campaign restructure. Team upskilling in parallel.' },
          { num: 'Weeks 7+', title: 'Scale', body: 'Monthly on-site cadence, weekly async reviews, quarterly strategy offsite.' },
        ],
      },
      {
        type: 'faq',
        eyebrow: 'FAQ',
        h2: 'Kolkata-specific questions I get',
        items: [
          { q: 'Do you only work with Kolkata brands?', a: 'No — about 40% of my clients are in Kolkata and West Bengal. The other 60% are across India, US, UK, UAE and SEA. Local roots, global reach.' },
          { q: 'Do you meet on-site?', a: 'Yes, weekly during active engagements. My base is New Town but I meet clients across Salt Lake, Park Street, Rajarhat, and Howrah.' },
          { q: 'What industries do you serve locally?', a: 'D2C (fashion, beauty, health), B2B SaaS, ed-tech, healthcare, and financial services. Zero real-estate leads or fake-ad-fraud campaigns.' },
          { q: 'How do you compare to a Kolkata digital marketing agency?', a: 'You are hiring the person — 16 years of operator experience — not a pod of juniors. No account management layer, no monthly retainer selling services you did not need. Direct.' },
        ],
      },
    ],
    related: [
      { slug: 'performance-marketing-consultant', title: 'Performance Marketing', desc: 'The global-tier service offering, delivered from Kolkata.' },
      { slug: 'growth-marketing-consultant', title: 'Growth Consulting', desc: 'Full-funnel growth for scaling Kolkata + India brands.' },
      { slug: 'paid-media-audit', title: 'Paid Media Audit', desc: 'A 10-day fixed-scope audit — the fastest way to start.' },
    ],
  },
};

export const landingPageSlugs = Object.keys(landingPages);

export const navigationServices = [
  { slug: 'performance-marketing-consultant', label: 'Performance Marketing' },
  { slug: 'growth-marketing-consultant', label: 'Growth Marketing' },
  { slug: 'paid-media-audit', label: 'Paid Media Audit' },
  { slug: 'ga4-gtm-attribution-consultant', label: 'GA4 & GTM Attribution' },
  { slug: 'server-side-tracking-consultant', label: 'Server-Side Tracking' },
  { slug: 'conversion-rate-optimization-consultant', label: 'CRO Consulting' },
  { slug: 'ai-marketing-consulting', label: 'AI Marketing' },
  { slug: 'performance-marketing-consultant-kolkata', label: 'Kolkata (Local)' },
];
